import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Download, Sparkles, MapPin } from "lucide-react";
import api, { resolveUrl } from "@/lib/api";
import { ProjectCard } from "@/components/ProjectCard";
import { Reveal } from "@/components/Reveal";
import { ProjectCardSkeleton, SkillCardSkeleton } from "@/components/Skeletons";
import { Seo } from "@/components/Seo";
import { Magnetic } from "@/components/Magnetic";
import Marquee from "react-fast-marquee";
import BrandLogo from "@/components/BrandLogo";

const staticSkillsMap = {
  "React": { icon: "⚛", exp: "30+ Projects" },
  "React 19": { icon: "⚛", exp: "30+ Projects" },
  "Next.js": { icon: "▲", exp: "12 Apps" },
  "TypeScript": { icon: "TS", exp: "4 Years" },
  "JavaScript (ES2023)": { icon: "JS", exp: "5+ Years" },
  "JavaScript": { icon: "JS", exp: "5+ Years" },
  "HTML5": { icon: "HTML", exp: "5+ Years" },
  "CSS3": { icon: "CSS", exp: "5+ Years" },
  "Accessibility (a11y)": { icon: "♿", exp: "WCAG AA" },
  "Responsive Design": { icon: "📱", exp: "Universal" },
  "Tailwind CSS": { icon: "🎨", exp: "Design Systems" },
  "Shadcn UI": { icon: "🧩", exp: "Components" },
  "Radix UI": { icon: "📦", exp: "Primitives" },
  "Framer Motion": { icon: "✨", exp: "Animations" },
  "Design Systems": { icon: "🎨", exp: "Scale" },
  "Node.js": { icon: "🟢", exp: "REST APIs" },
  "Express": { icon: "🚂", exp: "APIs" },
  "MongoDB": { icon: "🍃", exp: "NoSQL DB" },
  "GitHub Actions": { icon: "🛠️", exp: "Deployments" },
  "Docker": { icon: "🐳", exp: "CI/CD" },
  "Vite": { icon: "⚡", exp: "Build Tool" },
  "GraphQL": { icon: "📊", exp: "APIs" },
  "Flutter": { icon: "📱", exp: "Cross-platform" },
  "AWS": { icon: "☁️", exp: "Cloud Hosting" }
};

const categoryNameMap = {
  "Frontend Foundations": "Core Web Technologies",
  "React / Next.js Ecosystem": "Frontend Frameworks",
  "UI Libraries & Styling": "Design Systems",
  "Backend / MERN (Learning)": "Backend & APIs",
  "CI/CD & Tooling": "Developer Experience"
};

export default function Home() {
  const { profile } = useOutletContext();
  const [featured, setFeatured] = useState([]);
  const [skills, setSkills] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [failedLogos, setFailedLogos] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingSkills, setLoadingSkills] = useState(true);

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {}).finally(() => setLoadingProjects(false));
    api.get("/skills").then((r) => setSkills(r.data)).catch(() => {}).finally(() => setLoadingSkills(false));
    api.get("/client-logos").then((r) => setClientLogos(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: profile?.name || "Sandeep Varikuppala",
          jobTitle: profile?.role || "Frontend Developer",
          description: profile?.tagline,
          url: typeof window !== "undefined" ? window.location.origin : "",
          sameAs: [profile?.github, profile?.linkedin, profile?.twitter].filter(Boolean),
        }}
      />
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 pb-24 sm:pt-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-mono text-muted-foreground"
            >
              <Sparkles className="w-3.5 h-3.5" /> {profile?.hero_kicker || "Frontend Developer & UI Engineer"}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter leading-[1.02]"
            >
              I build <span className="font-semibold">premium</span> interfaces
              <br className="hidden sm:block" /> that feel <span className="italic">effortless</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              {profile?.tagline || "I craft fast, accessible, beautiful interfaces — and I'm growing into full-stack with the MERN ecosystem and CI/CD."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Magnetic>
                <Link to="/projects" data-testid="hero-cta-projects" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity">
                  Explore My Work <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Magnetic>
              <Magnetic>
                <a href={resolveUrl(profile?.resume_url || "#")} target="_blank" rel="noreferrer" data-testid="hero-cta-resume" className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 font-medium hover:bg-secondary/40 transition-colors">
                  <Download className="w-4 h-4" /> Download Resume
                </a>
              </Magnetic>
              <Link to="/contact" data-testid="hero-cta-contact" className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium text-muted-foreground hover:text-foreground transition-colors">
                Contact Me
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-6 sm:gap-8 lg:gap-6 xl:gap-8 border-t border-border/40 pt-8"
            >
              <div>
                <div className="text-3xl font-heading font-semibold tracking-tight">{profile?.years_experience || "5+"}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-medium">Years Experience</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-semibold tracking-tight">{profile?.projects_count || "30+"}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-medium">Projects Delivered</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-semibold tracking-tight">{profile?.happy_clients_count || "10+"}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-medium">Happy Clients</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-semibold tracking-tight">{profile?.responsive_builds_rate || "100%"}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1 font-medium">Responsive Builds</div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden glass p-3">
                <video
                  src={resolveUrl(profile?.avatar_video || "/video/sandeepvarikuppala1.mov")}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[420px] object-cover rounded-[1.6rem]"
                />
              </div>
              <div className="absolute -bottom-5 left-4 sm:-left-5 glass rounded-2xl px-5 py-4 max-w-[200px]">
                <div className="text-xs text-muted-foreground">Currently</div>
                <div className="text-sm font-medium mt-0.5">{profile?.about_status || "Leveling up MERN + CI/CD pipelines"}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Marquee Section (Directly on page background to ensure seamless edge fades with zero striping) */}
      <section className="relative w-full overflow-hidden py-8 mb-16">
        <div className="max-w-6xl mx-auto px-6 mb-4">
          <div className="text-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Worked with teams from</div>
        </div>
        <div className="relative w-full flex items-center overflow-hidden mask-marquee py-2">
          <Marquee 
            speed={40} 
            gradient={false} 
            pauseOnHover={true}
            play={true}
          >
            {(() => {
              const defaultPresets = [
                { name: "GOOGLE", logo_url: "" },
                { name: "APPLE", logo_url: "" },
                { name: "MICROSOFT", logo_url: "" },
                { name: "NETFLIX", logo_url: "" },
                { name: "AMAZON", logo_url: "" },
                { name: "ZOHO", logo_url: "" },
                { name: "STRIPE", logo_url: "" },
                { name: "META", logo_url: "" },
                { name: "VERCEL", logo_url: "" },
                { name: "GITHUB", logo_url: "" }
              ];

              const baseLogos = clientLogos.length > 0 ? clientLogos : defaultPresets;

              return baseLogos.map((item, idx) => {
                const key = item.id || `preset-${idx}`;
                const hasValidImage = item.logo_url && !failedLogos[key];

                return (
                  <div key={key} className="flex flex-col items-center gap-2.5 justify-center shrink-0 mx-4 sm:mx-8">
                    <div className="h-14 sm:h-20 w-32 sm:w-48 rounded-xl bg-white/95 dark:bg-white/90 border border-black/5 dark:border-white/5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.15)] flex items-center justify-center p-3 sm:p-4 hover:scale-[1.03] transition-all duration-300 select-none">
                      {hasValidImage ? (
                        <img
                          src={resolveUrl(item.logo_url)}
                          alt={item.name}
                          onError={() => setFailedLogos((prev) => ({ ...prev, [key]: true }))}
                          className="max-h-full max-w-full object-contain pointer-events-none"
                          style={{
                            filter: "drop-shadow(0px 0px 1px rgba(0,0,0,0.35))"
                          }}
                        />
                      ) : (
                        <BrandLogo name={item.name} />
                      )}
                    </div>
                    <span className="font-heading font-semibold text-[8px] sm:text-[9px] tracking-[0.15em] text-muted-foreground/50 hover:text-foreground transition-colors select-none text-center max-w-[90px] sm:max-w-[120px] truncate uppercase">
                      {item.name.toUpperCase()}
                    </span>
                  </div>
                );
              });
            })()}
          </Marquee>
        </div>
      </section>

      {/* Featured projects */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Selected work</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading tracking-tight mt-2">Featured case studies</h2>
            </div>
            <Link to="/projects" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              All projects <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loadingProjects
            ? Array.from({ length: 3 }).map((_, i) => <ProjectCardSkeleton key={i} />)
            : featured.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
        </div>
      </section>

      {/* Skills preview */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Toolkit</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading tracking-tight mt-2">A frontend core, growing full-stack</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loadingSkills
            ? Array.from({ length: 6 }).map((_, i) => <SkillCardSkeleton key={i} />)
            : skills.slice(0, 6).map((g, i) => {
                const categoryTitle = categoryNameMap[g.category] || g.category;
                return (
                  <Reveal key={g.id} delay={i * 0.06}>
                    <div className="glass rounded-2xl p-6 h-full flex flex-col justify-between">
                      <div>
                        <h3 className="font-heading text-lg font-medium text-foreground/90">{categoryTitle}</h3>
                        <div className="mt-5 space-y-2">
                          {g.items.slice(0, 4).map((it) => {
                            const skillName = it.split(":")[0].trim();
                            const staticData = staticSkillsMap[skillName] || {
                              icon: "🛠️",
                              exp: "Core"
                            };
                            return (
                              <div key={it} className="flex justify-between items-center py-2.5 border-b border-border/10 last:border-0">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <span className="text-base select-none shrink-0">{staticData.icon}</span>
                                  <span className="text-sm font-medium text-foreground/80 truncate">{skillName}</span>
                                </div>
                                <span className="font-mono text-[9px] uppercase tracking-wide text-muted-foreground bg-secondary/35 px-2 py-0.5 rounded border border-border/5 shrink-0">
                                  {staticData.exp}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
        </div>

        <div className="mt-12 flex justify-center">
          <Magnetic>
            <Link to="/skills" className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground border border-border/30 px-6 py-3 font-medium hover:bg-secondary/80 transition-colors">
              View All Skills <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Magnetic>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] glass p-10 sm:p-16 text-center">
            <h2 className="text-2xl sm:text-4xl font-heading tracking-tight">Have a role, project, or idea?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Let's talk about how I can help you ship something exceptional.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-medium hover:opacity-90 transition-opacity">Book a Call <ArrowUpRight className="w-4 h-4" /></Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform">Send a Message</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
